import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { CloseCircle } from 'asset';
import { DeleteDialog, LoadingPopup } from 'components/common';
import { Inputs } from 'components/form-control';
import yup from 'config/yup.custom';
import { NotifyService } from 'helpers';
import {
  CREATE_ADJUSTMENT_SURCHARGE_PAYLOAD,
  UPDATE_ADJUSTMENT_SURCHARGE_PAYLOAD,
} from 'models';
import { useEffect, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from 'store/hook';
import {
  createAdjustmentSurchargeAction,
  deleteAdjustmentSurchargeAction,
  updateAdjustmentSurchargeAction,
} from 'store/reducers/jobs/actionTypes';

interface PopupCreateEditSurchargeProps {
  open: boolean;
  onClosePopup: () => void;
  jobId: string;
}

interface IForms {
  adjustmentSurcharge: {
    id?: string | undefined;
    is_update?: boolean | undefined;
    description: string;
    parent_amount: string;
    sitter_amount: string;
  }[];
}

const schema = yup.object().shape({
  adjustmentSurcharge: yup.array().of(
    yup.object().shape({
      description: yup.string().required('Description is required'),
      parent_amount: yup
        .string()
        .required('Parent amount is required')
        .matches(
          /^[-]?\d+(\.\d{1,2})?$/,
          'Parent amount must be a valid number with format +/- 0.00'
        ),
      sitter_amount: yup
        .string()
        .required('Sitter amount is required')
        .matches(
          /^[-]?\d+(\.\d{1,2})?$/,
          'Sitter amount must be a valid number with format +/- 0.00'
        ),
    })
  ),
});

export const PopupCreateEditSurcharge = (
  props: PopupCreateEditSurchargeProps
) => {
  const { open, onClosePopup, jobId } = props;
  const dispatch = useAppDispatch();
  const { adjustmentSurcharge: dataAdjustment } = useAppSelector(
    (state) => state.jobsReducer
  );

  const [loadingPopup, setLoadingPopup] = useState<boolean>(false);
  const [openDeletePopup, setOpenDeletePopup] = useState<boolean>(false);
  const [itemDelete, setItemDelete] = useState<any | null>(null);

  const form = useForm<IForms>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      adjustmentSurcharge: [],
    },
  });

  const { reset, control, setValue, formState } = form;

  const {
    fields: adjustmentSurcharge,
    remove: removeItem,
    append: appendItem,
  } = useFieldArray({
    control,
    name: 'adjustmentSurcharge',
  });

  const _onClosePopup = () => {
    onClosePopup();
    reset({ adjustmentSurcharge: [] });
  };

  const onSubmit = async (values: IForms) => {
    setLoadingPopup(true);
    const dataCreate = {
      job_id: jobId,
      data: values.adjustmentSurcharge
        .filter((adj) => !adj.id)
        .map((adj) => {
          return {
            description: adj.description,
            parent_rate: Number(adj.parent_amount),
            sitter_rate: Number(adj.sitter_amount),
          };
        }),
    };
    const dataUpdate = {
      data: values.adjustmentSurcharge
        .filter((adj) => adj.id)
        .map((adj) => {
          return {
            id: adj.id ?? '',
            description: adj.description,
            sitter_rate: Number(adj.sitter_amount),
          };
        }),
    };

    handleBothActions(dataCreate, dataUpdate);
  };

  const handleBothActions = (
    dataCreate: CREATE_ADJUSTMENT_SURCHARGE_PAYLOAD,
    dataUpdate: UPDATE_ADJUSTMENT_SURCHARGE_PAYLOAD
  ) => {
    let createDone = dataCreate.data?.length === 0;
    let updateDone = dataUpdate.data?.length === 0;

    // when create done and update done then show notification
    const showNotification = () => {
      if (createDone && updateDone) {
        onClosePopup();
        setLoadingPopup(false);
        NotifyService.success('Save adjustment surcharge successfully');
      }
    };

    if (!createDone) {
      dispatch(
        createAdjustmentSurchargeAction({
          data: dataCreate,
          onSuccess: () => {
            createDone = true;
            showNotification();
          },
          onFailed: () => {
            setLoadingPopup(false);
          },
        })
      );
    }

    if (!updateDone) {
      dispatch(
        updateAdjustmentSurchargeAction({
          data: dataUpdate,
          onSuccess: () => {
            updateDone = true;
            showNotification();
          },
          onFailed: () => {
            setLoadingPopup(false);
          },
        })
      );
    }

    if (createDone && updateDone) {
      setLoadingPopup(false);
    }
  };

  const handleAddAdjustment = () => {
    appendItem({
      description: '',
      parent_amount: '',
      sitter_amount: '',
      is_update: false,
    });
  };

  const handleRemoveAdjustment = (item: any, index: number) => {
    setOpenDeletePopup(true);
    setItemDelete({ ...item, index });
  };

  useEffect(() => {
    setValue(
      'adjustmentSurcharge',
      dataAdjustment.records.map((adj) => {
        return {
          ...adj,
          is_update: true,
          parent_amount: adj.parent_rate.toString(),
          sitter_amount: adj.sitter_rate.toString(),
        };
      })
    );
  }, [dataAdjustment.records, setValue]);

  return (
    <Dialog
      open={open}
      sx={{
        '& .MuiPaper-root': {
          width: 527,
          borderRadius: 0,
        },
      }}
    >
      <Grid>
        <Grid
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 25px',
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '100%',
            }}
          >
            Adjustment Item
          </Typography>
          <IconButton
            onClick={_onClosePopup}
            sx={{
              padding: 0,
            }}
          >
            <CloseCircle />
          </IconButton>
        </Grid>
        <Divider />
        {loadingPopup && <LoadingPopup color="primary" />}
        <Divider />

        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Stack spacing="20px" p="25px 24px">
            <Typography fontSize={15} fontWeight={700} color={'grey1.dark'}>
              Adjustment Items
            </Typography>
            {adjustmentSurcharge.map((adj, index) => {
              return (
                <Stack spacing={2} key={adj.id}>
                  <Stack>
                    <Controller
                      name={`adjustmentSurcharge.${index}.description`}
                      control={form.control}
                      render={({ field }) => (
                        <Inputs
                          {...field}
                          title="Description"
                          form={form}
                          placeholder="Description"
                          errorsName={
                            formState.errors.adjustmentSurcharge?.[index]
                              ?.description?.message
                          }
                        />
                      )}
                    />
                  </Stack>
                  <Stack spacing={2} direction={'row'} width={'100%'}>
                    <Stack width={'100%'}>
                      <Controller
                        name={`adjustmentSurcharge.${index}.parent_amount`}
                        control={form.control}
                        render={({ field }) => (
                          <Inputs
                            {...field}
                            title="Parent Amount"
                            form={form}
                            placeholder="+/- 0.00"
                            errorsName={
                              formState.errors.adjustmentSurcharge?.[index]
                                ?.parent_amount?.message
                            }
                            disabled={adj.is_update}
                          />
                        )}
                      />
                    </Stack>
                    <Stack width={'100%'}>
                      <Controller
                        name={`adjustmentSurcharge.${index}.sitter_amount`}
                        control={form.control}
                        render={({ field }) => (
                          <Inputs
                            {...field}
                            title="Sitter Amount"
                            form={form}
                            placeholder="+/- 0.00"
                            errorsName={
                              formState.errors.adjustmentSurcharge?.[index]
                                ?.sitter_amount?.message
                            }
                          />
                        )}
                      />
                    </Stack>
                  </Stack>
                  <Button
                    variant="text"
                    color="primary"
                    sx={{
                      pl: 0,
                      pb: 0,
                      fontSize: '15px',
                      fontWeight: 400,
                      justifyContent: 'unset',
                      margin: '0px !important',
                      width: 150,
                    }}
                    onClick={() => handleRemoveAdjustment(adj as any, index)}
                  >
                    Remove Item
                  </Button>
                  <Divider />
                </Stack>
              );
            })}
            <Button
              variant="text"
              color="secondary"
              sx={{
                pl: 0,
                pt: 2,
                fontSize: '15px',
                fontWeight: 400,
                justifyContent: 'unset',
                margin: '0px !important',
                width: 150,
              }}
              onClick={handleAddAdjustment}
            >
              + Add Item
            </Button>
            <Stack direction="row" spacing={1}>
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                sx={{
                  height: 30,
                  width: '100%',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: 1.1,
                }}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        </form>
      </Grid>
      {openDeletePopup && (
        <DeleteDialog
          title={`Remove ${itemDelete.description}?`}
          subTitle="Are you sure you want to remove this adjustment item?"
          open={openDeletePopup}
          onClose={() => setOpenDeletePopup(false)}
          onDelete={() => {
            setOpenDeletePopup(false);
            setLoadingPopup(true);
            dispatch(
              deleteAdjustmentSurchargeAction({
                data: {
                  adjId: dataAdjustment.records[itemDelete.index].id,
                },
                onSuccess: () => {
                  removeItem(itemDelete.index);
                  setLoadingPopup(false);
                },
                onFailed: () => {
                  setLoadingPopup(false);
                },
              })
            );
          }}
        />
      )}
    </Dialog>
  );
};

export default PopupCreateEditSurcharge;
